/* eslint-disable no-use-before-define */
/* eslint-disable class-methods-use-this */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useEventStreamRequest } from '@/hooks/useEventStreamRequest';
import { MessageRole, MessageStatus, TextMessage, SuggestionMessage, Messages } from '@/types/XAiMessage';
import { type Error, ErrorCode, type SendContent, type Success, SuccessCode, type SessionData } from '@/types';
import { request } from '@/utils/request';
import { safeJsonParse } from '@/utils/index';
import { SESSION_PAGE_SIZE, API_SUCCESS_CODE } from '@/constants';
import { fetchAppConfig, fetchSessionList, fetchSessionDetail, closeSession, updateSessionTitle } from '@/services/api';
import { getNodeTemplate } from '@/utils/workflowNode';
// 图标导入
import documentIcon from '@/assets/document.png';
import completedIcon from '@/assets/completed.png';
import completedExpandIcon from '@/assets/completed-black.png';
import documentExpandIcon from '@/assets/document-black.png';
import qaIcon from '@/assets/qa.png';
import qaExpandIcon from '@/assets/qa-black.png';
import skillNoIcon from '@/assets/skillNo.png';
import skillNoExpandIcon from '@/assets/skillNo-black.png';
import toolIcon from '@/assets/tools.png';
import toolExpandIcon from '@/assets/tools-black.png';

// ==================== 类型定义 ====================
export type ContentType = 'TEXT' | 'IMAGE' | 'FILE' | 'JSON';

export interface ImageUrl {
  url: string;
  name: string;
}

export interface FileUrl {
  url: string;
  fileName: string;
  suffix: string;
}

export interface Content {
  text?: string;
  imageUrl?: ImageUrl;
  fileUrl?: FileUrl;
}

export interface ContentDef {
  description: string;
  contentType: ContentType;
  content: Content;
}

export type Contents = ContentDef[];

export enum ActionType {
  recallKnowledgeStart = 'RECALL_KNOWLEDGE_START',
  recallKnowledgeEnd = 'RECALL_KNOWLEDGE_END',
  invokeToolStart = 'INVOKE_AGENT_TOOL_START',
  invokeToolEnd = 'INVOKE_AGENT_TOOL_END',
  reasoning = 'REASONING',
  reasoningStart = 'REASONING_START',
  reasoningEnd = 'REASONING_END',
  componentStart = 'COMPONENT_START',
  componentStream = 'COMPONENT_STREAM',
  componentEnd = 'COMPONENT_END',
  suggest = 'FOLLOW_UP',
  response = 'RESPONSE',
  finish = 'FINISHED',
}

export enum ProcessStatus {
  start = 'START',
  end = 'END',
}

export enum ActionAvailability {
  disabled = 'disabled',
  enabled = 'enabled',
  remote = 'remote',
}

export type ThinkStep = any;

export interface Action {
  name: string;
  description: string;
  jsonSchema: string;
  available?: ActionAvailability;
}

export interface BaseFields {
  textSpeed?: number;
  onChatDone?: () => void;
}

export interface UseAgentGeneratorProps {
  url: string;
  token: string;
  config: any;
  textSpeed?: number;
  onError?: (error: Error) => void;
  onSuccess?: (data: Success) => void;
  onMessage?: (content: string, data: Messages) => void;
}

// ==================== 消息处理器 ====================
class MessageProcessor {
  private textSpeed: number;

  constructor(textSpeed = 60) {
    this.textSpeed = textSpeed;
  }

  // 获取知识图标
  private getKnowledgeIcons(knowledgeType: string) {
    return knowledgeType === 'DOCUMENT'
      ? { icon: documentIcon, expandIcon: documentExpandIcon }
      : { icon: qaIcon, expandIcon: qaExpandIcon };
  }

  // 获取工具图标
  private getToolIcons(toolType: string) {
    return toolType === 'SKILL'
      ? { icon: skillNoIcon, expandIcon: skillNoExpandIcon }
      : { icon: toolIcon, expandIcon: toolExpandIcon };
  }

  // 获取工作流图标(暂时不做区分)
  private getWorkflowIcon() {
    return { icon: skillNoIcon, expandIcon: skillNoExpandIcon };
  }

  // 更新工具执行事件
  private updateExecution(
    messages: Messages[],
    messageIndex: number,
    uniqueId: string,
    cost: number,
    name?: string,
  ) {
    const msg = messages[messageIndex] as TextMessage;
    if (msg?.execute) {
      const execution = msg.execute.find((item) => item.id === uniqueId);
      if (execution) {
        execution.extra.cost = cost ? (cost / 1000).toFixed(2) : 0;
        if (name) execution.name = name;
      }
    }
  }

  // 查找最后一个待处理的AI消息
  private findLastPendingAIMessage(messages: Messages[]): [number, TextMessage | null] {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === MessageRole.assistant
          && msg.type === 'TextMessage'
          && (msg.status === 'init' || msg.status === 'pending')) {
        return [i, msg as TextMessage];
      }
    }
    return [-1, null];
  }

  // 创建新的AI消息
  private createNewAIMessage(): TextMessage {
    return {
      id: uuidV4(),
      type: 'TextMessage',
      createdAt: new Date(),
      role: MessageRole.assistant,
      content: { text: '' },
      execute: [],
      thinks: '',
      status: MessageStatus.init,
    };
  }

  // 确保存在待处理的AI消息
  private ensureAIMessageExists(messages: Messages[]): [Messages[], number, TextMessage] {
    const [index, existingMsg] = this.findLastPendingAIMessage(messages);

    if (index !== -1 && existingMsg) {
      return [[...messages], index, { ...existingMsg }];
    }

    const newMessage = this.createNewAIMessage();
    const newMessages = [...messages, newMessage];
    return [newMessages, newMessages.length - 1, newMessage];
  }

  // 处理建议
  handleSuggestion(messages: Messages[], data: any): Messages[] {
    const { content } = data;
    const newMessages = [...messages];
    const suggestIndex = newMessages.findIndex((item) => item.type === 'SuggestionMessage');

    if (suggestIndex === -1) {
      const newSuggestion: SuggestionMessage = {
        id: uuidV4(),
        type: 'SuggestionMessage',
        createdAt: new Date(),
        status: MessageStatus.done,
        role: MessageRole.suggestion,
        content: [{ key: uuidV4(), description: content }],
      };
      newMessages.push(newSuggestion);
    } else {
      const suggestion = newMessages[suggestIndex] as SuggestionMessage;
      suggestion?.content?.push({
        key: uuidV4(),
        description: content,
      });
    }

    return newMessages;
  }

  // 处理思考过程
  handleReasoning(messages: Messages[], data: any): Messages[] {
    const { content } = data;
    const [newMessages, index, aiMsg] = this.ensureAIMessageExists(messages);

    // 确保消息状态正确
    const updatedMsg: Messages = {
      ...aiMsg,
      thinks: (aiMsg.thinks || '') + content,
      status: aiMsg.status === MessageStatus.init ? MessageStatus.pending : aiMsg.status,
    };

    const updatedMessages = [...newMessages];
    updatedMessages[index] = updatedMsg;

    return updatedMessages;
  }

  // 处理思考过程结束
  handleReasoningEnd(messages: Messages[]): Messages[] {
    const [newMessages, index, aiMsg] = this.ensureAIMessageExists(messages);

    // 确保消息状态正确
    const updatedMsg: Messages = {
      ...aiMsg,
      thinks: `${aiMsg.thinks || ''}\n`,
      status: aiMsg.status === MessageStatus.init ? MessageStatus.pending : aiMsg.status,
    };

    const updatedMessages = [...newMessages];
    updatedMessages[index] = updatedMsg;

    return updatedMessages;
  }

  // 处理知识库调用开始
  handleRecallKnowledgeStart(messages: Messages[], data: any): Messages[] {
    const [newMessages, index, aiMsg] = this.ensureAIMessageExists(messages);
    const { uniqueId = uuidV4() } = data || {};
    const { args } = data?.data || {};

    aiMsg.execute = aiMsg.execute || [];

    aiMsg.execute.push({
      id: uniqueId,
      createdAt: new Date(),
      type: 'ActionExecutionMessage',
      name: '开始搜索知识库',
      arguments: args,
      extra: {
        ...data,
        icon: documentIcon,
        expandIcon: documentExpandIcon,
      },
    });

    newMessages[index] = aiMsg;
    return newMessages;
  }

  // 处理知识库调用结束
  handleRecallKnowledgeEnd(messages: Messages[], data: any): Messages[] {
    const [index, _aiMsg] = this.findLastPendingAIMessage(messages);
    if (index === -1) return messages;
    const { uniqueId = uuidV4(), cost } = data || {};

    const newMessages = [...messages];
    this.updateExecution(newMessages, index, uniqueId, cost, '已搜索知识库');
    return newMessages;
  }

  // 处理工具调用开始
  handleInvokeToolStart(messages: Messages[], data: any): Messages[] {
    const [newMessages, index, aiMsg] = this.ensureAIMessageExists(messages);
    const { uniqueId = uuidV4() } = data || {};
    const { args, toolType } = data?.data || {};

    aiMsg.execute = aiMsg.execute || [];
    const icons = this.getToolIcons(toolType);

    aiMsg.execute.push({
      id: uniqueId,
      createdAt: new Date(),
      type: 'ActionExecutionMessage',
      name: toolType === 'SKILL' ? '已调用工作流' : '已调用工具',
      arguments: args,
      extra: {
        ...data,
        ...icons,
      },
    });

    newMessages[index] = aiMsg;
    return newMessages;
  }

  // 处理工具调用结束
  handleInvokeToolEnd(messages: Messages[], data: any): Messages[] {
    const [index, _aiMsg] = this.findLastPendingAIMessage(messages);
    const { uniqueId = uuidV4() } = data || {};
    const { toolType } = data?.data || {};
    if (index === -1) return messages;

    const newMessages = [...messages];
    this.updateExecution(newMessages, index, uniqueId, data.cost, toolType === 'SKILL' ? '已调用工作流组件' : '已调用工具');
    return newMessages;
  }

  // 处理工作流组件开始
  handleComponentStart(messages: Messages[], data: any): Messages[] {
    const [newMessages, index, aiMsg] = this.ensureAIMessageExists(messages);
    const { content } = data || {};
    const { nodeId, args, componentType } = content || {};
    const { name } = getNodeTemplate(componentType);
    aiMsg?.execute?.push({
      id: nodeId || uuidV4(),
      createdAt: new Date(),
      type: 'ActionExecutionMessage',
      name: name ? `开始调用${name}组件` : '开始调用工作流组件',
      arguments: args,
      extra: {
        ...data,
        ...this.getWorkflowIcon(),
      },
    });

    newMessages[index] = aiMsg;
    return newMessages;
  }

  // 处理工作流组件流消息
  handleComponentStream(messages: Messages[], data: any): Messages[] {
    const [newMessages, index, aiMsg] = this.ensureAIMessageExists(messages);
    const { nodeId = uuidV4, content } = data || {};
    const { processData } = content || {};
    aiMsg.raw = data.raw;
    aiMsg.stepContent = aiMsg.stepContent || [];
    aiMsg.stepContent.push({
      id: nodeId,
      content: processData,
    });

    // 消息状态改为接收中
    if (aiMsg.status === MessageStatus.init) {
      aiMsg.status = MessageStatus.pending;
    }

    newMessages[index] = aiMsg;
    return newMessages;
  }

  // 处理工作流组件结束
  handleComponentEnd(messages: Messages[], data: any): Messages[] {
    const [index, _aiMsg] = this.findLastPendingAIMessage(messages);
    const { content } = data || {};
    const { nodeId, componentType } = content || {};
    const { name } = getNodeTemplate(componentType);
    if (index === -1) return messages;
    const newMessages = [...messages];
    this.updateExecution(newMessages, index, nodeId, data.cost, name ? `已调用完成${name}组件` : '已调用工作流组件');
    return newMessages;
  }

  // 处理响应正文
  handleResponse(messages: Messages[], data: any): Messages[] {
    const [newMessages, index, aiMsg] = this.ensureAIMessageExists(messages);
    const { contentType, content, success, errorMsg } = data;
    if (success) {
      aiMsg.raw = data.raw;
      // 消息状态改为接收中
      if (aiMsg.status === MessageStatus.init) {
        aiMsg.status = MessageStatus.pending;
      }
      // 文本类型数据
      if (contentType === 'TEXT' && 'text' in aiMsg.content) {
        aiMsg.content.text += content;
      }

      // JSON 格式数据
      if (contentType === 'JSON') {
        aiMsg.content.text += JSON.stringify(content);
      }
    } else {
      aiMsg.status = MessageStatus.failed;
      aiMsg.content.text = `<span style="color: red;">❌ ${errorMsg}</span>`;
    }

    newMessages[index] = aiMsg;
    return newMessages;
  }

  // 处理完成
  handleFinish(messages: Messages[], data: any): Messages[] {
    const [index, _aiMsg] = this.findLastPendingAIMessage(messages);
    const { data: { uniqueId = uuidV4() } = {}, cost, needExecuteDetail } = data || {};
    if (index === -1) return messages;

    const newMessages = [...messages];
    const msg = newMessages[index] as TextMessage;
    if (needExecuteDetail) {
      msg.execute = msg.execute || [];
      msg.execute.push({
        id: uniqueId || uuidV4(),
        createdAt: new Date(),
        type: 'ActionExecutionMessage',
        name: '运行完毕',
        extra: {
          ...data,
          cost: cost ? (cost / 1000).toFixed(2) : 0,
          icon: completedIcon,
          expandIcon: completedExpandIcon,
        },
      });
    }

    msg.status = MessageStatus.done;
    return newMessages;
  }

  // 处理响应数据
  processResponse(messages: Messages[], responseData: any): Messages[] {
    if (!responseData?.data?.outputs) return messages;
    let response = responseData.data.outputs;
    if (typeof response === 'string') {
      response = safeJsonParse(response, {
        action: ActionType.response,
        data: {
          contentType: 'TEXT',
          content: '解析响应数据时出错，请重试',
          success: false,
          errorMsg: '响应数据格式错误',
        },
      });
    }

    const { action, data = {} } = response;

    switch (action) {
      case ActionType.suggest:
        return this.handleSuggestion(messages, data);

      case ActionType.reasoning:
        return this.handleReasoning(messages, data);

      case ActionType.reasoningEnd:
        return this.handleReasoningEnd(messages);

      case ActionType.componentStart:
        return this.handleComponentStart(messages, data);

      case ActionType.componentStream:
        return this.handleComponentStream(messages, data);

      case ActionType.componentEnd:
        return this.handleComponentEnd(messages, data);

      case ActionType.recallKnowledgeStart:
        return this.handleRecallKnowledgeStart(messages, data);

      case ActionType.recallKnowledgeEnd:
        return this.handleRecallKnowledgeEnd(messages, data);

      case ActionType.invokeToolStart:
        return this.handleInvokeToolStart(messages, data);

      case ActionType.invokeToolEnd:
        return this.handleInvokeToolEnd(messages, data);

      case ActionType.response:
        return this.handleResponse(messages, data);

      case ActionType.finish:
        return this.handleFinish(messages, data);

      default:
        return messages;
    }
  }
}

const useAgentGenerator = (params: UseAgentGeneratorProps) => {
  const {
    url,
    token,
    config,
    onError,
    onMessage,
    onSuccess,
    textSpeed = 55,
    ...rest
  } = params;

  // 聊天记录
  const [messages, setMessages] = useState<Messages[]>([]);
  // 聊天记录分页设置
  // const [messagePagination, setMessagePagination] = useState({ total: 0, pageNum: 1, pageSize: 999 });
  // 会话记录
  const [sessionList, setSessionList] = useState<SessionData[]>([]);
  // 会话请求是否加载中
  // const [sessionLoading, setSessionLoading] = useState<boolean>(false);
  // 会话分页设置
  const [sessionPagination, setSessionPagination] = useState({ total: 0, pageNum: 1, pageSize: SESSION_PAGE_SIZE });
  // 当前会话
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  // 应用 配置
  const [appInfo, setAppInfo] = useState<any>(null);
  // 请求 配置
  const requestInfo = useRef<{ requestId?: string; sensitiveWords?: string[] }>({});

  const { appNo } = config || {};

  // 消息处理器
  const messageProcessor = useMemo(() => new MessageProcessor(textSpeed), [textSpeed]);

  // 初始化时获取配置
  useEffect(() => {
    if (url && config) {
      const initConfig = async () => {
        try {
          const { data, result } = await fetchAppConfig({ url, ...config });
          if (result.code === API_SUCCESS_CODE) {
            setAppInfo(data);

            // 成功回调
            onSuccess?.({
              code: SuccessCode.APP_CONFIG_INIT_SUCCESS,
              message: '初始化配置成功',
            });
          } else {
            onError?.({
              code: result.code || 'UNKNOWN_ERROR',
              message: result.message || '获取应用配置失败',
              raw: result,
            });
          }
        } catch (error) {
          onError?.({
            code: ErrorCode.NETWORK_ERROR,
            message: '获取应用配置失败',
          });
        }
      };
      initConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, config]);

  const { start, loading, responseData, stop } = useEventStreamRequest({
    streaming: true,
    textSpeed,
    onClose: () => {
      // close的时机有可能在setMessages之前，所以需要延迟一下
      setTimeout(() => {
        if (requestInfo.current?.sensitiveWords && requestInfo.current?.requestId) {
          request.post(url);
        }
      }, 500);
    },
    onOpen(res) {
      console.log('res-----open:', res);
    },
  });

  // 数据流处理
  useEffect(() => {
    if (!responseData) return;

    setMessages((prev) => messageProcessor.processResponse(prev, responseData));
  }, [responseData, messageProcessor]);

  // 添加对话(非交互触发)
  const setMessage = useCallback(async (messageType: string, content: any) => {
    switch (messageType) {
      case 'prologue': {
        setMessages((prev) => {
          const prologueIndex = prev.findIndex((item) => item.extra?.prologue);

          if (prologueIndex === -1) {
            const newMessage: TextMessage = {
              type: 'TextMessage',
              id: uuidV4(),
              createdAt: new Date(),
              role: MessageRole.assistant,
              status: MessageStatus.done,
              content: { text: content },
              extra: { prologue: true, noFooter: true },
            };
            return [newMessage, ...prev];
          }

          return prev.map((item) => (item.extra?.prologue && item.content && 'text' in item.content
            ? { ...item, content: { text: content } } as any
            : item));
        });
        break;
      }

      case 'suggest': {
        setMessages((prev) => {
          const suggestIndex = prev.findIndex((item) => item.type === 'SuggestionMessage');
          const prologueIndex = prev.findIndex((item) => item.extra?.prologue);

          const newSuggestion: SuggestionMessage = {
            id: uuidV4(),
            type: 'SuggestionMessage',
            createdAt: new Date(),
            status: MessageStatus.done,
            role: MessageRole.suggestion,
            content,
          };

          if (suggestIndex === -1) {
            const newMessages = [...prev];
            const insertIndex = prologueIndex === -1 ? 0 : prologueIndex + 1;
            newMessages.splice(insertIndex, 0, newSuggestion);
            return newMessages;
          }

          return prev.map((item) => (item.type === 'SuggestionMessage'
            ? { ...item, content }
            : item));
        });
        break;
      }

      default:
        console.warn(`Unknown message type: ${messageType}`);
    }
  }, []);

  // 简单对话
  const chat = useCallback(async (content: SendContent, extra?: any, insert = true) => {
    if (insert) {
      const newMessage: TextMessage = {
        type: 'TextMessage',
        id: uuidV4(),
        createdAt: new Date(),
        role: MessageRole.user,
        status: MessageStatus.done,
        extra,
        content,
      };

      setMessages((prev) => {
        const newMessages = prev.filter((item) => item.type !== 'SuggestionMessage');
        newMessages.push(newMessage);
        return newMessages;
      });
    }

    const requestBody: any = {
      appParams: {
        inputs_msg: content.text,
      },
      sessionId: currentSessionId,
    };

    start(
      `${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sendMsgStreaming`,
      requestBody,
      {
        Authorization: `Bearer ${token}`,
      },
    );
  }, [appNo, currentSessionId, start, token, url]);

  // 重新生成方法
  const reChat = useCallback(() => {
    setMessages((prev) => {
      const newMessages = [...prev];

      // 删除最新 AI 消息
      const lastAIIndex = newMessages?.findLastIndex(
        (m) => m.role === MessageRole.assistant && m.type === 'TextMessage'
      );
      if (lastAIIndex !== -1) {
        newMessages.splice(lastAIIndex, 1);
      }

      // 删除建议消息
      const suggestIndex = newMessages.findIndex((item) => item.type === 'SuggestionMessage');
      if (suggestIndex !== -1) {
        newMessages.splice(suggestIndex, 1);
      }

      // 找到最后一条用户消息重新发送
      const lastUserMsg = newMessages.findLast(
        (m) => m.role === MessageRole.user && m.type === 'TextMessage',
      ) as TextMessage | undefined;

      if (lastUserMsg?.content?.text) {
        setTimeout(() => {
          chat(lastUserMsg.content, lastUserMsg.extra, false);
        }, 0);
      }

      return newMessages;
    });
  }, [chat]);

  // 停止对话
  const stopChat = () => {
    stop();
    setMessages((prev) => prev.map((m) => {
      // 所有进行中的消息变更为结束
      if (m.status === MessageStatus.pending || m.status === MessageStatus.init) {
        const newMsg = { ...m, status: MessageStatus.done };
        if (m.type === 'TextMessage' && m.status === MessageStatus.init) {
          (newMsg as TextMessage).content.text = '<span style="color: #cbcbcb;">用户已取消</span>';
        }
        return newMsg;
      }
      return m;
    }));
  };

  // 初始化历史记录
  const formatMessages = (data: any, init = false) => {
    setMessages((prev) => {
      const initMessagesList: Messages[] = [];
      data.forEach((item: any) => {
        const { content, messageTime, role } = item || {};

        if (role === 'USER') {
          const newMessage: Messages = {
            type: 'TextMessage',
            id: uuidV4(),
            createdAt: messageTime,
            role: MessageRole.user,
            status: MessageStatus.done,
            content: {
              text: content,
            },
          };

          initMessagesList.push(newMessage);
        } else if (role === 'AI') {
          const newMessage: TextMessage = {
            id: uuidV4(),
            type: 'TextMessage',
            createdAt: messageTime,
            role: MessageRole.assistant,
            content: { text: content },
            execute: [],
            status: MessageStatus.done,
            isLike: item.isLike,
            raw: item.raw,
          };

          initMessagesList.push(newMessage);
        }
      });

      if (init) {
        return [...initMessagesList];
      }
      return [...prev, ...initMessagesList];
    });
  };

  // 初始化开场白
  const initPrologue = async () => {
    const { onboardingInfo = {} } = appInfo;
    // eslint-disable-next-line camelcase
    const { suggested_questions = [], prologue = '' } = onboardingInfo || {};

    // 初始化开场白
    if (prologue) {
      setMessage('prologue', prologue);
    } else {
      setMessages((prev) => prev.filter((item) => !item.extra?.prologue));
    }

    // 过滤空内容
    const formatSuggests = suggested_questions.filter((item: string) => item);

    if (formatSuggests.length > 0) {
      const suggestMessages = formatSuggests.map((item: string, index: number) => ({
        key: index + 1,
        description: item,
      }));
      // 页面为空时展示开场白预设问题
      const hasMessages = messages.some((item) => (!item.extra?.prologue && item.type !== 'SuggestionMessage'));
      if (!hasMessages) {
        setMessage('suggest', suggestMessages);
      }
    } else {
      setMessages((prev) => prev.filter((item) => !(item.type === 'SuggestionMessage')));
    }
  };

  // 获取聊天记录
  const initAppConversations = async (fetchDetail = false) => {
    try {
      const { data = [], pagination, result } = await fetchSessionList({
        url,
        appNo,
        token,
        pageNum: sessionPagination.pageNum,
        pageSize: sessionPagination.pageSize,
      });

      if (result?.code === API_SUCCESS_CODE) {
        if (data.length > 0) {
          const formatSessions = data.map((item) => {
            return {
              key: item.sessionId,
              label: item.title || '新对话',
              timestamp: new Date(item.startTime).getTime(),
              messages: [],
              meta: {
                ...item,
              },
            };
          });

          setSessionList(formatSessions);
          // 设置分页信息
          setSessionPagination({ pageNum: sessionPagination.pageNum, pageSize: sessionPagination.pageSize, total: pagination?.total || 0 });
          const { sessionId } = data?.[0];
          fetchDetail && setCurrentSession(sessionId);
        } else {
          // 页面报错 初始化
          setSessionList([]);
          setCurrentSessionId(uuidV4());
          initPrologue();
        }
        // 成功回调
        onSuccess?.({
          code: SuccessCode.APP_MESSAGES_INIT_SUCCESS,
          message: '初始化聊天记录成功',
        });
      } else {
        initPrologue();
      }
    } catch {
      //
    }
  };

  // 设置当前会话详情
  const setCurrentSession = async (sessionId: string) => {
    if (sessionId) {
      if (sessionId === currentSessionId) {
        return;
      }
      setCurrentSessionId(sessionId); // 设置当前会话 id;
      const { data, result } = await fetchSessionDetail({
        url,
        appNo,
        sessionId,
        token,
      });

      if (result?.code === API_SUCCESS_CODE) {
        const sessionsData = data?.reduce((acc: any[], cur) => {
          const sessionMessages = Array.isArray(cur.sessionMessages) ? cur.sessionMessages : [];
          const msgs = sessionMessages.map((msg: any) => ({
            ...msg,
            raw: cur,
            isLike: cur?.isLike,
          }));
          return [...acc, ...msgs];
        }, []);

        if (sessionsData.length > 0) {
          formatMessages(sessionsData, true);
        }
      }
    } else {
      // 兼容 无 session异常
      setCurrentSessionId(uuidV4());
    }
  };

  // 删除会话
  const deleteSession = async (sessionId: string) => {
    if (!sessionId) return;
    const { result } = await closeSession({
      url,
      appNo,
      sessionId,
      token,
    });
    if (result?.code === API_SUCCESS_CODE) {
      // 删除成功后，重新获取会话列表
      initAppConversations(false);
      // 如果删除的是当前会话，则创建新的会话
      if (sessionId === currentSessionId) {
        createNewChat();
      }
    }
  };

  // 更新会话名称
  const updateSession = async (sessionId: string, title: string) => {
    if (!sessionId && !title) return;
    const { result } = await updateSessionTitle({
      url,
      appNo,
      title,
      sessionId,
      token,
    });
    if (result?.code === API_SUCCESS_CODE) {
      // 编辑成功后，重新获取会话列表
      initAppConversations(false);
    }
  };

  // 生成新对话
  const createNewChat = () => {
    stopChat(); // 停止聊天
    setMessages([]); // 清空所有消息
    initPrologue(); // 初始化开场白和预置问题
    setCurrentSessionId(uuidV4()); // 设置会话 id
  };

  // 初始化应用
  useEffect(() => {
    if (appInfo) {
      // 初始化历史数据
      initAppConversations(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appInfo]);

  return {
    appInfo,
    messages,
    loading,
    content: responseData.content,
    sessionList,
    currentSessionId,
    sessionPagination,
    initAppConversations,
    reChat,
    chat,
    createNewChat,
    setCurrentSessionId,
    setCurrentSession,
    updateSession,
    deleteSession,
    setMessage,
    setMessages,
    stopChat,
    initPrologue,
  };
};

export default useAgentGenerator;
